import { FC, RefAttributes, forwardRef } from 'react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';

import cn from 'classnames';

import './Radio.scss';

interface WithClassNames {
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
}

export interface RadioRootProps extends RadixRadioGroup.RadioGroupProps, RefAttributes<HTMLDivElement> {}
export const RadioRoot: FC<RadioRootProps> = (props) => <RadixRadioGroup.Root {...props} />;

export interface RadioItemProps extends RadixRadioGroup.RadioGroupItemProps, WithClassNames {}
export const RadioItem: FC<RadixRadioGroup.RadioGroupItemProps> = forwardRef<HTMLButtonElement, RadioItemProps>(
  (props, ref) => <RadixRadioGroup.Item {...props} ref={ref} />,
);
RadioItem.displayName = 'RadioItem';

export interface RadioIndicatorProps extends RadixRadioGroup.RadioIndicatorProps, WithClassNames {}
export const RadioIndicator: FC<RadioIndicatorProps> = ({ classNames, ...props }) => (
  <RadixRadioGroup.Indicator {...props} className={cn(classNames)} />
);

export const RadioButton: FC<WithClassNames> = ({ classNames }) => {
  const classContainer = 'lsux-rb-container';

  const classContainerRb = `${classContainer}__rb`;
  const cnContainerRb = cn(
    {
      [`${classContainerRb}`]: true,
    },
    classNames,
  );

  return (
    <div className={cnContainerRb}>
      <RadixRadioGroup.Indicator className="lsux-rb__indicator" />
    </div>
  );
};

export interface RadioProps extends Omit<RadioItemProps, 'value' | 'type'> {
  /**
   * The value of the radio button.
   * @default 'default'
   */
  value?: string;
  /**
   * Label to the right of the button
   */
  rightLabel?: string;
  /**
   * Label to the left of the button
   */
  leftLabel?: string;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * Ref for the underlying input
   */
  ref?: React.ForwardedRef<HTMLButtonElement>;
}

const RadioComponent = forwardRef<HTMLButtonElement, RadioProps>(
  ({ value = 'default', rightLabel, leftLabel, classNames = [], ...props }, ref: RadioProps['ref']) => {
    const classContainer = 'lsux-rb-container';
    const cnContainer = cn(
      {
        [`${classContainer}`]: true,
      },
      classNames,
    );

    return (
      <RadioItem ref={ref} {...props} className={cnContainer} value={value}>
        {leftLabel && <span className="mr-2">{leftLabel}</span>}
        <RadioButton />
        {rightLabel && <span className="ml-2">{rightLabel}</span>}
      </RadioItem>
    );
  },
) as FC<RadioProps>;

/**
 * A radio button is a quick way to extract a single coded answer from our users by presenting multiple possibilities and allowing only one option to be chosen.
 */
RadioComponent.displayName = 'Radio';

type RadioRootComponent = FC<RadioRootProps>;
type RadioItemComponent = FC<RadioItemProps>;
type RadioIndicatorComponent = FC<RadioIndicatorProps>;

type RadioComp = FC<RadioProps> & {
  Root: RadioRootComponent;
  Item: RadioItemComponent;
  Indicator: RadioIndicatorComponent;
};

export const Radio = RadioComponent as RadioComp;

Radio.Root = RadioRoot;
Radio.Item = RadioItem;
Radio.Indicator = RadioIndicator;
